import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { pageTitle, sectionTitle, sectionWrapper, titleWrapper } from '../../../../styles/sectionStyles'
import { ValidatableTextInput } from '../../../../components/validatable-inputs/text-input'
import { ValidatableDayPicker } from '../../../../components/validatable-inputs/day-picker'
import { css } from '../../../../../styled-system/css'
import { button } from '../../../../styles/buttonStyles'
import { z } from 'zod'
import { useMemo, useState } from 'react'
import { BaseDialog } from '../../../../components/dialog'
import { useCreateDistributionCampaign, useGetAllFundSources } from '../../../../libs/admin/hooks'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { Timestamp } from '@bufbuild/protobuf'
import { endOfDay } from 'date-fns'

const newCampaignSchema = z.object({
  campaignName: z.string().min(1, 'キャンペーン名を入力してください').default(''),
  pointsPerPerson: z.number().min(1, '1以上の数値を入力してください').default(0),
  numberOfRecipients: z.number().min(1, '1以上の数値を入力してください').default(0),
  expirationDate: z.date({
    message: '有効期限を設定してください',
  }).refine(
    (date) => date > new Date(),
    { message: '有効期限は今日より後の日付を選択してください' }
  ).nullable().refine((date) => date !== null, { message: '有効期限を設定してください' }),
})

type NewCampaignType = z.infer<typeof newCampaignSchema>

export const Route = createFileRoute('/admin/points/$fundSourceId/newCampaign')(
  {
    component: RouteComponent,
  },
)

const useData = () => {
  const { fundSourceId } = Route.useParams()
  const { data: fundSources, isLoading: isFundSourceLoading, error: fundSourceError } = useGetAllFundSources()
  const fundSource = fundSources?.find(fs => fs.id === fundSourceId)
  return {
    fundSourceId,
    fundSource,
    isLoading: isFundSourceLoading,
    error: fundSourceError,
  }
}

const BreadCrumb = () => {
  const { fundSourceId, fundSource } = useData()
  if (!fundSource) {
    return null
  }

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <Link
        to="/admin/points"
        className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}
      >
        ポイント種類
      </Link>
      <span
        className={css({
          color: 'text.tertiary',
        })}
      >
        /
      </span>
      <Link
        to="/admin/points/$fundSourceId"
        params={{ fundSourceId }}
        className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}
      >
        {fundSource.name}
      </Link>
      <span
        className={css({
          color: 'text.tertiary',
        })}
      >
        /
      </span>
      <span
        className={css({
          color: 'text.tertiary',
        })}
      >
        配布ポイント作成
      </span>
    </div>
  )
}

function RouteComponent() {
  const { fundSourceId } = Route.useParams()
  const navigate = useNavigate()
  const createDistributionCampaign = useCreateDistributionCampaign()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const [campaign, setCampaign] = useState<Partial<NewCampaignType>>({
    campaignName: '',
    pointsPerPerson: 0,
    numberOfRecipients: 0,
  })

  const parseResult = useMemo(() => {
    return newCampaignSchema.safeParse(campaign)
  }, [campaign])

  const fieldErrors = parseResult.success ? {} : parseResult.error?.flatten().fieldErrors ?? {}

  const totalPoints = useMemo(() => {
    const points = campaign.pointsPerPerson ?? 0
    const recipients = campaign.numberOfRecipients ?? 0
    return points * recipients
  }, [campaign.pointsPerPerson, campaign.numberOfRecipients])

  const handleConfirmCreate = async () => {
    if (!parseResult.success) return

    try {
      const result = await createDistributionCampaign.mutateAsync({
        fundSourceId,
        title: parseResult.data.campaignName,
        distributionCount: BigInt(parseResult.data.numberOfRecipients),
        distributionBalanceAmount: BigInt(parseResult.data.pointsPerPerson),
        balanceExpiresAt: parseResult.data.expirationDate ? Timestamp.fromDate(parseResult.data.expirationDate) : undefined,
      })

      if (result) {
        setShowConfirmDialog(false)
        navigate({ to: '/admin/points/$fundSourceId', params: { fundSourceId } })
      }
    } catch (error) {
      console.error('Failed to create distribution campaign:', error)
    }
  }

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>配布ポイント作成</h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        position: 'relative',
      })}>
        <BreadCrumb />
        <div className={sectionWrapper()}>
          <div className={sectionTitle()}>配布ポイント詳細</div>
          <ValidatableTextInput
            type={'text'}
            title="キャンペーン名"
            description='配布ポイント作成履歴に表示する管理用のキャンペーン名を入力してください。'
            value={campaign.campaignName ?? ''}
            onChange={(value) => setCampaign(prev => ({ ...prev, campaignName: value }))}
            isRequired
            isValid={!fieldErrors.campaignName}
            validationMessage={fieldErrors.campaignName?.[0] ?? 'OK'}
          />
          <ValidatableTextInput
            type={'number'}
            title="配布ポイント（1件あたり）"
            description='1件あたりの配布ポイント数を入力してください。'
            value={campaign.pointsPerPerson?.toString() ?? '0'}
            onChange={(value) => setCampaign(prev => ({ ...prev, pointsPerPerson: Number.parseInt(value) || 0 }))}
            isRequired
            isValid={!fieldErrors.pointsPerPerson}
            validationMessage={fieldErrors.pointsPerPerson?.[0] ?? 'OK'}
          />
          <ValidatableTextInput
            type={'number'}
            title="配布件数"
            value={campaign.numberOfRecipients?.toString() ?? '0'}
            onChange={(value) => setCampaign(prev => ({ ...prev, numberOfRecipients: Number.parseInt(value) || 0 }))}
            isRequired
            isValid={!fieldErrors.numberOfRecipients}
            validationMessage={fieldErrors.numberOfRecipients?.[0] ?? 'OK'}
          />
          <ValidatableDayPicker
            title="ポイント有効期限"
            description='指定した日付の23:59:59まで有効です'
            value={campaign.expirationDate ?? undefined}
            onChange={(date) => setCampaign(prev => ({
              ...prev,
              expirationDate: date ? endOfDay(date) : undefined
            }))}
            isRequired
            isValid={!fieldErrors.expirationDate}
            validationMessage={fieldErrors.expirationDate?.[0] ?? 'OK'}
            placeholder='有効期限'
          />
        </div>
        <div className={css({
          ...sectionWrapper.raw(),
          backgroundColor: 'None',
        })}>
          <div>合計配布ポイント</div>
          <div>{totalPoints.toLocaleString()} pts</div>
        </div>
        <button
          type="submit"
          className={button({ color: 'primary' })}
          onClick={() => setShowConfirmDialog(true)}
          disabled={!parseResult.success}
        >
          作成
        </button>
      </div>

      <BaseDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        title="配布ポイントを作成しますか?"
      >
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        })}>
          <p className={css({
            fontSize: '14px',
            color: 'text.secondary',
            margin: 0,
          })}>
            作成した配布ポイントは、一覧ページからダウンロードしてください。
          </p>

          <div className={css({
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
          })}>
            <button
              type="button"
              className={button({ color: 'ghost' })}
              onClick={() => setShowConfirmDialog(false)}
            >
              もどる
            </button>
            <button
              type="button"
              className={button({ color: 'primary' })}
              onClick={handleConfirmCreate}
              disabled={createDistributionCampaign.isPending}
            >
              配布ポイント作成
            {createDistributionCampaign.isPending && <LoadingOverlay size='sm'/>}
            </button>
          </div>
        </div>
      </BaseDialog>
    </div>
  )
}
